import { createReducer, isAnyOf } from '@reduxjs/toolkit';

import { handleLocalStorage } from '@utils';

import { errorBuilder, loadersBuilder, logoutBuilder } from '@modules/builders';
import { loginActions } from '@modules/login';

import userActions from './actions';
import initialState from './state';
import type * as UserTypes from './types';
import { VerificationStatusesEnum, IbanStatusesEnum } from './types';

const { setItem } = handleLocalStorage;

export const userReducer = createReducer(initialState, builder => {
  builder.addCase(userActions.initBranch, (state, action) => {
    const referralCode = action.payload?.data?.data_parsed?.referral_code;

    if (referralCode) {
      state.referralCode = referralCode;
    }
  });

  builder.addCase(userActions.setCaptcha, (state, action) => {
    state.captcha = action.payload;
  });

  builder.addCase(userActions.skipKYC, state => {
    state.isSkippedKYC = true;
  });

  builder.addCase(userActions.setStrongPasswordModal, (state, action) => {
    setItem('isStrongModal', action.payload);
    state.isStrongPasswordModal = action.payload;
  });

  builder.addCase(userActions.clearKYCToken, state => {
    delete state.KYC;
  });

  builder.addCase(userActions.setIsCountry, (state, action) => {
    state.isCountryModal = action.payload;
  });
  builder.addCase(userActions.setIsIntroModal, (state, action) => {
    state.isIntroModal = action.payload;
  });

  builder.addCase(userActions.editUser.fulfilled, (state, action) => {
    if (state.data) {
      state.data.language_code = action.payload.data.language_code;
      state.data.fiat_currency = action.payload.data.fiat_currency;
      state.data.send_marketing_material = action.payload.data.send_marketing_material;
      state.data.strong_security = action.payload.data.strong_security;
      state.isStrongPasswordModal = action.payload.data.strong_security;
    }
  });

  builder.addCase(userActions.changeEmailVerifyEmail.fulfilled, (state, action) => {
    if (state.data && action.payload.data.email) {
      state.data.email = action.payload.data.email;
    }
  });

  builder.addCase(userActions.getZHDisclaimer.fulfilled, (state, action) => {
    state.isZHDisclaimer = action.payload.data.zh_disclaimer_show;
  });

  builder.addCase(userActions.changePhoneVerifyPhone.fulfilled, (state, action) => {
    if (state.data && action.payload.data.phone) {
      state.data.phone = action.payload.data.phone;
    }
  });

  builder.addCase(userActions.accessKYCToken.fulfilled, (state, action) => {
    state.KYC = action.payload.data;
  });

  builder.addCase(userActions.changeNewPassword.fulfilled, state => {
    if (state.data) {
      state.data.has_password = true;
    }
  });
  builder.addCase(userActions.getOTPSetup.fulfilled, (state, action) => {
    const { otp_seed, otp_url } = action.payload.data;

    if (otp_url && otp_seed) {
      state.otp = {
        seed: otp_seed,
        url: otp_url,
      };
    }
  });

  builder.addCase(userActions.clearErrors, state => {
    state.errors = {};
  });

  builder.addCase(userActions.setIbanStatus, state => {
    if (state.data) {
      state.data.checks.document = VerificationStatusesEnum.under_review;
      state.data.checks.proof_of_address = VerificationStatusesEnum.under_review;
      state.data.iban_status = IbanStatusesEnum.forbidden;
    }
  });

  builder.addCase(loginActions.resetPasswordVerifyOTP.fulfilled, (state, action) => {
    state.otpKey = action.payload.data.key;
  });

  builder.addMatcher(
    isAnyOf(userActions.getUserData.fulfilled, userActions.getUserDataPolling.fulfilled),
    (state, action) => {
      state.data = action.payload.data;
      if (!state.sessionId) state.sessionId = action.payload.data.session_id;
    }
  );

  loadersBuilder('user', builder);
  logoutBuilder(initialState, builder);
  errorBuilder('user', builder);
});

export { userActions, UserTypes };
